import type { DisplayStatus, KnownStatus, Lane } from '../types';

/**
 * Status priority drives lane sorting (§2.4). Lower number = earlier in
 * the lane. The numbers are arbitrary as long as they preserve the order
 * documented in the requirements.
 */
export const STATUS_PRIORITY: Record<DisplayStatus, number> = {
  pending: 1,
  received: 2,
  in_progress: 3,
  waiting_for_pickup: 4,
  completed: 5,
};

export const LEFT_LANE_STATUSES: DisplayStatus[] = ['pending', 'received', 'in_progress'];
export const RIGHT_LANE_STATUSES: DisplayStatus[] = ['waiting_for_pickup', 'completed'];

/** Human-readable labels shown on the card / pill (§2.3). */
export const STATUS_LABELS: Record<DisplayStatus, string> = {
  pending: 'Queued',
  received: 'Queued',
  in_progress: 'Preparing',
  waiting_for_pickup: 'Ready',
  completed: 'Collected',
};

const KNOWN_STATUSES: ReadonlySet<KnownStatus> = new Set<KnownStatus>([
  'pending',
  'received',
  'in_progress',
  'waiting_for_pickup',
  'completed',
  'cancelled',
]);

/** Returns the lane an order with this status renders in. */
export function laneFor(status: DisplayStatus): Lane {
  return LEFT_LANE_STATUSES.indexOf(status) !== -1 ? 'left' : 'right';
}

/**
 * Narrows a raw API/event string to a known status, or returns null when
 * the value is unrecognised so the caller can drop the order (§2.3).
 */
export function parseStatus(raw: string | null | undefined): KnownStatus | null {
  if (!raw) return null;
  return KNOWN_STATUSES.has(raw as KnownStatus) ? (raw as KnownStatus) : null;
}

export function isDisplayStatus(status: KnownStatus): status is DisplayStatus {
  return status !== 'cancelled';
}
